import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { getToken } from "../../lib/tokenStorage";
import { toast } from "sonner";
import { Download, MapPin, Calendar, Clock, Tag, Sparkles, Youtube } from "lucide-react";

const CUR_SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };

// Turn a YYYY-MM-DD into "Sat, 15 Mar 2026" so participants + partners
// glance the day-of-week — activities cluster on weekends so this matters.
const prettyDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
};

export default function ActivitiesTab() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/partner-portal/activities")
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]));
  }, []);

  const downloadPdf = async (a) => {
    // Open a synchronous placeholder tab BEFORE the async fetch so Safari /
    // Chrome popup blockers don't kill the download. Then swap its URL to
    // the blob once ready (same trick used elsewhere in the portal).
    const win = window.open("", "_blank");
    setBusy(a.id);
    try {
      const res = await fetch(`${API}/activities/${a.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      // Also trigger a save-to-disk with a friendly filename
      const a2 = document.createElement("a");
      a2.href = url;
      a2.download = `aii-activity-${(a.name || "activity").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a2); a2.click(); a2.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      if (win) win.close();
      toast.error(e?.message || "Could not download PDF");
    } finally {
      setBusy("");
    }
  };

  if (rows === null) return (
    <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]" data-testid="pa-activities-loading">
      Loading activities…
    </div>
  );

  if (rows.length === 0) return (
    <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]" data-testid="pa-activities-empty">
      <Sparkles size={22} className="text-brand mx-auto mb-2"/>
      No approved activities yet in your placement cities. Your Aii coordinator adds new experiences every week — check back soon.
    </div>
  );

  // Group by country → city so a partner with participants in India + Nepal
  // sees each destination cluster distinctly.
  const groups = new Map();
  for (const a of rows) {
    const k = `${a.country || "—"} · ${a.city || "—"}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(a);
  }

  return (
    <div className="space-y-5" data-testid="pa-activities">
      <div className="aii-card p-4 border-l-4 border-brand">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
          <Sparkles size={11}/> Approved by Aii · shareable with your participants
        </div>
        <p className="text-xs text-[color:var(--aii-muted)] mt-1">
          Every activity below has been vetted by our BDOM team and matches at least one of your participants&apos; placement cities. Click <b>Download PDF</b> to send it to your participant.
        </p>
      </div>

      {[...groups.entries()].map(([label, list]) => (
        <div key={label} className="space-y-2" data-testid={`pa-act-grp-${label}`}>
          <div className="text-[11px] uppercase tracking-widest font-bold text-brand pb-1 border-b border-[color:var(--aii-border)]">
            {label} <span className="text-[10px] font-normal text-[color:var(--aii-muted)] normal-case tracking-normal">· {list.length} {list.length === 1 ? "activity" : "activities"}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {list.map(a => (
              <div key={a.id} data-testid={`pa-act-${a.id}`} className="aii-card p-4 hover:border-brand hover:shadow-sm transition flex flex-col">
                {a.images?.[0] && (
                  <div className="-mx-4 -mt-4 mb-3 aspect-video overflow-hidden rounded-t-md bg-slate-100">
                    <img src={a.images[0]} alt="" className="w-full h-full object-cover"/>
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-base leading-tight">{a.name}</div>
                    {a.category && (
                      <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)]">
                        <Tag size={9} className="inline -mt-0.5 mr-0.5"/>{a.category}
                      </span>
                    )}
                  </div>
                  {(a.price || 0) > 0 && (
                    <div className="text-right shrink-0">
                      <div className="text-lg font-display font-semibold text-brand leading-none">{CUR_SYM[a.currency] || a.currency}{(a.price || 0).toLocaleString()}</div>
                      <div className="text-[10px] text-[color:var(--aii-muted)] uppercase tracking-widest">per person</div>
                    </div>
                  )}
                </div>

                <div className="mt-3 text-xs text-[color:var(--aii-text-2)] space-y-1">
                  {a.date && <div className="flex items-center gap-1.5"><Calendar size={11} className="text-brand"/> {prettyDate(a.date)}</div>}
                  {a.time && <div className="flex items-center gap-1.5"><Clock size={11} className="text-brand"/> {a.time}</div>}
                  {a.venue && <div className="flex items-center gap-1.5"><MapPin size={11} className="text-brand"/> {a.venue}</div>}
                  {a.youtube_url && (
                    <a href={a.youtube_url} target="_blank" rel="noopener noreferrer"
                       className="flex items-center gap-1.5 text-red-600 hover:underline">
                      <Youtube size={11}/> Watch teaser
                    </a>
                  )}
                </div>

                {a.notes && (
                  <p className="mt-2 text-xs text-[color:var(--aii-muted)] line-clamp-3 whitespace-pre-line">{a.notes}</p>
                )}

                <div className="mt-3 pt-3 border-t border-[color:var(--aii-border)] flex items-center justify-end">
                  <button
                    data-testid={`pa-act-pdf-${a.id}`}
                    onClick={() => downloadPdf(a)}
                    disabled={busy === a.id}
                    className="text-xs px-3 py-1.5 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5 disabled:opacity-50">
                    <Download size={12}/> {busy === a.id ? "Preparing…" : "Download PDF"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
